import { generateEventUniqueId, normalizeData, hashForFacebook } from './helper';

/**
 * Extract ad platform from query parameters
 * @param {Object} queryParams - URL query parameters
 * @returns {string|null} - Ad platform name or null
 */
const advertisePlatform = (queryParams) => {
  if (!queryParams || Object.keys(queryParams).length === 0) return null;
  
  if (queryParams.gclid) return "google";
  if (queryParams.fbclid) return "facebook";
  if (queryParams.utm_source) {
    const source = queryParams.utm_source.toString().toLowerCase();
    if (source.includes("google")) return "google";
    if (source.includes("facebook") || source.includes("fb")) return "facebook";
    return source;
  }
  return null;
};

/**
 * Hash user data for Facebook standard format
 * @param {string} name - Full name to be split into first and last name
 * @param {string} email - Email address
 * @returns {Object} - Hashed user data object
 */
const hashUserData = async (full_name, email) => {
  const [firstName, lastName] = full_name ? full_name.split(" ") : [null, null];
  
  return {
    fn: firstName ? await hashForFacebook(normalizeData(firstName)) : null,
    ln: lastName ? await hashForFacebook(normalizeData(lastName)) : null,
    em: email ? await hashForFacebook(normalizeData(email)) : null,
  };
};

/**
 * Push ad tracking event to dataLayer
 * @param {Object} params - Event parameters
 * @param {string} params.event - Event name (required)
 * @param {Object} params.location - Location object with pathname and search (required)
 * @param {string} [params.ad_platform] - Override ad platform (optional)
 * @param {string} [params.user_id] - User ID (optional, falls back to localStorage.getItem("user_id"))
 * @param {string} [params.name] - User name (optional, falls back to localStorage.getItem("user_fullName"))
 * @param {string} [params.email] - User email (optional, falls back to localStorage.getItem("email"))
 * @param {string} [params.role] - User role (optional)
 * @param {string} [params.organization_id] - Organization ID (optional)
 * @param {string} [params.organization_name] - Organization name (optional)
 * @param {string} [params.plan] - Subscription plan (optional)
 * @param {string} [params.interval] - Subscription interval (optional)
 * @param {number} [params.current_users] - Current user count (optional)
 * @param {number} [params.amount] - Amount (optional)
 */
export async function pushAdTrackingEvent({
  event,
  location,
  ad_platform,
  user_id,
  full_name,
  email,
  selectedOrganization,
  plan,
  interval,
  current_users,
  amount,
  error_type,
  error_message,
}) {
  if (!event || !location) {
    console.error('pushAdTrackingEvent: event and location are required');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Extract query parameters from location
  const urlParams = new URLSearchParams(location.search);
  const queryParams = Object.fromEntries(urlParams.entries());

  // Determine ad_platform (props > query params > localStorage)
  const detectedAdPlatform = ad_platform || advertisePlatform(queryParams);
  const storedAdPlatform = localStorage.getItem("ad_platform");
  
  // Store in localStorage if detected and not already stored
  if (detectedAdPlatform && !storedAdPlatform) {
    localStorage.setItem("ad_platform", detectedAdPlatform);
  }
  
  // Use detected platform, fallback to stored, or null
  const finalAdPlatform = detectedAdPlatform || storedAdPlatform;

  // Build base event data with mandatory fields
  const eventData = {
    event, 
    event_id: generateEventUniqueId(),
    page: location.pathname + location.search,
    ad_platform: finalAdPlatform,
  };

  // Add optional user fields (props override localStorage)
  const finalUserId = user_id || localStorage.getItem("user_id");
  const finalName = full_name || localStorage.getItem("user_fullName");
  const finalEmail = email || localStorage.getItem("email");

  if (finalUserId) {
    eventData.user_id = finalUserId;
  }

  // Add user_data if full_name or email is provided
  if (finalName || finalEmail) {
    eventData.user_data = await hashUserData(finalName, finalEmail);
  }

  if (finalEmail) {
    eventData.email = finalEmail;
  }

  if (finalName) {
    eventData.name = finalName;
  }

  // Add optional organization fields
  if (selectedOrganization?.role) {
    eventData.role = selectedOrganization?.role;
  }

  if (selectedOrganization?.id) {
    eventData.organization_id = selectedOrganization?.id;
  }

  if (selectedOrganization?.name) {
    eventData.organization_name = selectedOrganization?.name;
  }

  // Add optional subscription fields
  if (plan) {
    eventData.plan = plan;
  }

  if (interval) {
    eventData.interval = interval;
  }

  if (current_users !== undefined) {
    eventData.current_users = current_users;
  }

  if (amount !== undefined) {
    eventData.amount = amount;
  }

  if (error_type !== undefined) {
    eventData.error_type = error_type;
  }

  if (error_message !== undefined) {
    eventData.error_message = error_message;
  }

  // Push to dataLayer
  window.dataLayer.push(eventData);
}
